import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, mediaUrl, money, apiErr } from "@/lib/api";
import { Button } from "@/lib/ui";

export default function PaymentModal({ order, onClose, onDone }) {
  const ref = useRef();
  const [qr, setQr] = useState(null);
  const [shot, setShot] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/seller/payment-qr").then(({ data }) => setQr(data)).catch(() => setQr({ qr_url: "", note: "" }));
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShot(data.url); toast.success("Screenshot uploaded");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!shot) { toast.error("Please upload a clear payment screenshot"); return; }
    setBusy(true);
    try {
      await api.post("/seller/payments", { type: order.type, plan_name: order.plan_name, amount: order.amount, screenshot: shot });
      setDone(true);
      onDone && onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 ds-scroll" onClick={(e) => e.stopPropagation()} data-testid="payment-modal">
        {done ? (
          <div className="py-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-600"><i className="fa-solid fa-hourglass-half text-2xl" /></div>
            <h2 className="mt-4 font-head text-xl font-extrabold text-slate-900">Waiting for Admin Approval</h2>
            <p className="mt-2 text-sm text-slate-500">Your payment for <b>{order.plan_name}</b> ({money(order.amount)}) has been submitted with your screenshot. Our team will verify and activate it shortly.</p>
            <Button className="mt-5 w-full" onClick={onClose} data-testid="payment-done-close">Done</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h2 className="font-head text-xl font-extrabold text-slate-900">Scan & Pay</h2>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="mt-1 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">{order.plan_name} · <b className="text-slate-900">{money(order.amount)}</b></div>

            <div className="mt-4 flex flex-col items-center">
              {qr && qr.qr_url ? (
                <img src={mediaUrl(qr.qr_url)} alt="Payment QR" className="h-48 w-48 rounded-lg border border-slate-200 object-contain p-2" data-testid="payment-qr-image" />
              ) : (
                <div className="flex h-48 w-48 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 text-center text-xs text-slate-400">
                  <i className="fa-solid fa-qrcode mb-2 text-3xl" />Payment QR not set yet.<br />Please contact admin.
                </div>
              )}
              {qr && qr.note && <p className="mt-2 text-sm font-semibold text-slate-700">{qr.note}</p>}
            </div>

            <p className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-500">Upload clear payment screenshot</p>
            <div onClick={() => ref.current.click()} data-testid="payment-screenshot-drop"
              className="mt-1 flex aspect-video cursor-pointer items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:border-orange-400">
              {busy && !shot ? <i className="fa-solid fa-spinner fa-spin text-slate-400" /> :
                shot ? <img src={mediaUrl(shot)} alt="Screenshot" className="h-full w-full object-cover" /> :
                  <span className="text-xs text-slate-400"><i className="fa-solid fa-cloud-arrow-up mr-1" />Click to upload</span>}
            </div>
            <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />

            <Button className="mt-4 w-full py-3" onClick={submit} disabled={busy} variant="green" data-testid="payment-submit">
              <i className="fa-solid fa-paper-plane" /> Submit for Approval
            </Button>

            <p data-testid="payment-disclaimer" className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
              <i className="fa-solid fa-circle-info mr-1 text-slate-400" />
              deadstock.live charges only for weblisting and maintenance of the marketplace, payments are non-refundable on any grounds.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
